import { Listbox, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { Fragment } from "react";

import { cn } from "@/lib/utils";
import Checkbox from "@/components/ui/checkbox";

interface MultiSelectOption {
  value: string;
  label: string;
  color?: string;
  backgroundColor?: string;
  icon?: React.ReactNode;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onValueChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  triggerIcon?: React.ReactNode;
  "aria-label"?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selectedValues,
  onValueChange,
  placeholder = "Select",
  className,
  triggerIcon,
  "aria-label": ariaLabel,
}) => {
  const handleToggle = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onValueChange(newValues);
  };

  const hasSelection = selectedValues.length > 0;

  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      const option = options.find(opt => opt.value === selectedValues[0]);
      return option?.label || placeholder;
    }
    return `${selectedValues.length} selected`;
  };

  return (
    <div className={cn("relative", className)}>
      <Listbox value={selectedValues} onChange={onValueChange} multiple>
        <Listbox.Button
          className={cn(
            "flex h-8 w-auto min-w-[80px] items-center justify-between gap-1.5 rounded-md border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-0",
            hasSelection ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900",
          )}
          aria-label={ariaLabel}
        >
          <div className="flex items-center gap-1.5">
            {triggerIcon && triggerIcon}
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <ChevronDownIcon className="h-3 w-3 opacity-60" />
        </Listbox.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Listbox.Options className="absolute left-0 top-full z-50 mt-1 max-h-72 w-56 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
            {options.map(option => (
              <Listbox.Option
                key={option.value}
                value={option.value}
                className={({ active }) =>
                  cn(
                    "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm transition-colors",
                    active ? "bg-gray-50" : "",
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <div
                      className="flex-shrink-0"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleToggle(option.value);
                      }}
                    >
                      <Checkbox
                        id={`checkbox-${option.value}`}
                        checked={selected}
                        className="m-0 h-4 w-4"
                      />
                    </div>
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      {option.icon && (
                        <div
                          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded ${
                            option.backgroundColor || "bg-gray-50"
                          }`}
                        >
                          {option.icon}
                        </div>
                      )}
                      <span className="truncate" title={option.label}>
                        {option.label}
                      </span>
                    </div>
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </Listbox>
    </div>
  );
};

export default MultiSelect;
export type { MultiSelectOption };
